import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgxPaginationModule } from 'ngx-pagination';
import { Observable, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { SessionKeys } from 'src/app/enumeration/sessionKeys';
import { CheckAccessRoles } from 'src/app/utils/check-access-roles';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';
import { Refrsh } from 'src/app/utils/refresh';
import { StoreData } from 'src/app/utils/store_data';
import { CreditNoteSession } from './../../models/credit_note_session_model';
import { CreditNotesApiService } from './../../services/credit_notes_service';
import { useSessionState } from 'src/app/utils/session-state.util';
declare var $: any

interface AdvancedFilterState {
  page: number;
  search: string;
  dateRange: { start: string | null; end: string | null };
  selectedRoles: string[];
}
@Component({
  selector: 'app-credit-note',
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    NgbModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe
  ],
  templateUrl: './credit-note.component.html',
  styleUrls: ['./credit-note.component.css']
})
export class CreditNoteComponent {

  // Configured in one line. Kept secure in memory and sessionStorage!
  private stateManager = useSessionState<AdvancedFilterState>('credit_note_filter_state', {
    page: 0,
    search: 'ALL',
    dateRange: { start: null, end: null },
    selectedRoles: []
  });

  protected filters = this.stateManager.state;

  creditNotesList: any = []
  pageSize = PageSize.CREDIT_NOTES_LIST
  get page(): number { return this.filters().page; }
  allTotal = 0
  loading = false
  createCreditNotesForm: FormGroup<any>;
  updateCreditNotesForm: FormGroup<any>;
  uploadZipCreditNoteForm: FormGroup<any>
  isError = false
  errorMessage = ''
  selectedFile!: File
  selectedZipFile!: File
  displayStyle: string = 'none';
  creditNotesName = ''
  nameList: any[] = []
  get loginName(): string { return this.filters().search; }
  searchName: any = ''
  isDealer = false
  isUploading = false
  isCreateUpdateRemoveAccess: boolean = false;
  fileList: any = []
  isUploadError: any
  uploadCreditNoteForm: FormGroup<any>

  constructor(private fb: FormBuilder,
    private router: Router,
    private refresh: Refrsh,
    private store: StoreData,
    private service: CreditNotesApiService,
    private dateFormat: DateFormatPipe,
    private checkAccessRoles: CheckAccessRoles) {

    this.createCreditNotesForm = this.fb.group({
      dealerLoginName: ['', Validators.required],
      description: ['', Validators.required],
      effectiveDate: ['', Validators.required],
      file: ['', Validators.required],
    });
    this.updateCreditNotesForm = this.fb.group({
      effectiveDate: ['', Validators.required],
      description: ['', Validators.required],
    });
    this.uploadCreditNoteForm = this.fb.group({
      dealerLoginName: ['', Validators.required],
      effectiveDate: ['', Validators.required],
      file: ['', Validators.required],
    });
    this.uploadZipCreditNoteForm = this.fb.group({
      effectiveDate: ['', Validators.required],
      file: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.isCreateUpdateRemoveAccess = this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.FINANCE_UPLOAD_ADMIN])

    if (this.store.getIsDealer()) {
      this.isDealer = true
      const dealerName = this.store.getLoginName();
      this.stateManager.updateState(() => ({ search: dealerName }));
      this.searchCreditNotesListByName(this.page)
    } else {
      this.initData()
    }
  }

  async initData() {
    try {
      await this.getAllCreditNotesList();
      await this.getNameList();
    } catch (error) {
      console.error('An error occurred:', error);
    }
  }

  async getAllCreditNotesList() {
    this.loading = true
    return new Promise<void>((resolve, reject) => {
      this.service.getCreditNotesList(this.loginName, this.page, this.pageSize).subscribe({
        next: (response) => {
          this.creditNotesList = response.cNMdoRUploadRespLst
          this.allTotal = response.allTotal;
        },
        error: (e) => {
          this.loading = false
          if (e) {
            resolve()
            alert(e)
          }
        },
        complete: () => {
          // this.getNameList()
          this.loading = false
          resolve()
        },
      })
    });

  }

  async getNameList() {
    this.loading = true
    return new Promise<void>((resolve, reject) => {
      this.service.getNameList().subscribe({
        next: (response) => {
          this.nameList = response
          if (this.filters().search) {
            if (this.filters().search != 'ALL') {
              this.searchName = this.nameList.filter(item => item[0].toLowerCase() === this.filters().search.toLowerCase()).toString();
            }else{
              this.searchName = ''
            }
          }
        },
        error: (e) => {
          this.loading = false
          if (e) {
            resolve()
            alert(e)
          }
        },
        complete: () => {
          resolve()
          this.loading = false
        },
      })
    });
  }

  searchList = (text$: Observable<string>): Observable<string[]> => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => term.length < 1 ? []
        : this.nameList.filter(item => item[1].toLowerCase().startsWith(term.toLowerCase()))
      )
    );
  }

  onFileSelected(event: any) {
    this.selectedFile = event.target.files[0]
  }

  onFileSelectedMulti(event: any) {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files.length > 0) {
      this.fileList = Array.from(inputElement.files)
    }
  }

  async uploadFile() {
    this.isUploading = true
    const name = this.uploadCreditNoteForm.get('dealerLoginName')?.value
    const dealerLoginName = name[0]
    var date = this.dateFormat.convertObjectToDDMMYYYY(this.uploadCreditNoteForm.get('effectiveDate')?.value)
    var errorFileName: any[] = []

    for (const file of this.fileList) {
      const name = file.name
      const [fileName, des] = name.split("_");
      const description = des.substring(0, des.lastIndexOf('.jpg'))
      const value = await this.createUploadFileByFile(dealerLoginName, description, date, file)
      // console.log(value);

      if (String(value).includes('Error')) {
        errorFileName.push(name + value)
      } else {
        console.log("Success for", name);
      }
    }
    this.isUploading = false
    $('#uploadCSVModal').modal('hide');
    if (errorFileName.length > 0) {
      alert(errorFileName.join(" , "))
    } else {
      alert("Success")
    }
    this.clearCreditNotesList()
  }

  async createUploadFileByFile(dealerLoginName: any, description: any, date: any, file: any) {
    console.log("Api call ", file.name);

    // return true
    return new Promise((resolve, reject) => {
      this.service.getCreateCreditNotes(file, dealerLoginName, description, date).subscribe({
        next: (value) => {
          console.log('Success Api for', file.name);
          resolve('Success'); // Resolve the promise with the value
        },
        error: (e) => {
          if (e) {
            resolve('Error : ' + e)
          }
        },
        complete: () => {
        },
      });
    })
  }

  createCreditNotes() {
    this.isUploading = true
    const name = this.createCreditNotesForm.get('dealerLoginName')?.value
    const dealerLoginName = name[0]
    var description = this.createCreditNotesForm.get('description')?.value
    var date = this.dateFormat.convertObjectToDDMMYYYY(this.createCreditNotesForm.get('effectiveDate')?.value)

    this.service.getCreateCreditNotes(this.selectedFile, dealerLoginName, description, date).subscribe({
      next: (value) => {
        $('#createCreditNotesModal').modal('hide');
        alert('Success')
      },
      error: (e) => {
        this.isUploading = false
        if (e) {
          this.isError = true
          this.errorMessage = e
        }
      },
      complete: () => {
        this.isUploading = false
        this.isError = false
        this.clearCreditNotesList()
      },
    })
  }
  onFileSelectedZip(event: any) {
    this.selectedZipFile = event.target.files[0]
  }

  uploadZipFile() {

    if (this.selectedZipFile.size > 250 * 1024 * 1024) {
      alert("File size exceeds the limit (250 MB). Please select a smaller file.")
      return;
    }

    this.isUploading = true

    var date = this.dateFormat.convertObjectToDDMMYYYY(this.uploadZipCreditNoteForm.get('effectiveDate')?.value);

    this.service.getUploadZipFileRebate(this.selectedZipFile, date).subscribe({
      next(value) {
        $('#uploadZipCreditNoteModal').modal('hide');
        alert('Success')
      },
      error: (e) => {
        this.isUploading = false
        if (e) {
          this.isError = true
          this.errorMessage = e
        }
      },
      complete: () => {
        this.isUploading = false
        this.isError = false
        this.clearCreditNotesList()
      },
    })
  }

  checkZipUploadStatus() {
    this.service.getUploadZipFileRebateStatus().subscribe({
      next(value) {
        alert(value.Message)
      },
      error: (e) => {
        if (e) {
          alert(e)
        }
      },
      complete: () => {
      },
    })
  }

  searchCreditNotesListByName(page: number) {
    this.loading = true

    let nameToSearch = this.loginName;

    if (Array.isArray(this.searchName) && this.searchName.length > 0) {
      nameToSearch = this.searchName[0];
    }

    this.stateManager.updateState(() => ({ search: nameToSearch, page: page }));

    this.service.getCreditNotesList(this.loginName, page, this.pageSize).subscribe({
      next: (response) => {
        
        if (response?.pageTotal == 0 && page > 0) {
          const fallbackPage = page - 1;

          this.searchCreditNotesListByName(fallbackPage);
          return;
        }

        this.creditNotesList = response?.cNMdoRUploadRespLst || [];
        this.allTotal = response?.allTotal || 0;
      },
      error: (e) => {
        this.loading = false
        if (e) {
          alert(e)
        }
      },
      complete: () => {
        this.loading = false
      },
    })
  }

  clearSearch() {
    this.stateManager.resetState();
    this.clearCreditNotesList()
  }

  clearCreditNotesList() {

    if (this.loginName !== 'ALL') {
      this.searchCreditNotesListByName(this.page)
    } else {
      this.searchName = ''
      this.getAllCreditNotesList();
    }
  }

  openPDF(data: any) {
    this.router.navigate(['/nav/imageView', 'CreditNote', data.name, data.fileName])
  }

  openUpdateModel(data: any) {
    this.displayStyle = 'block'
    this.creditNotesName = data.name
    this.updateCreditNotesForm.patchValue({
      description: data.description,
      effectiveDate: this.dateFormat.convertDateToObject(data.effectiveDate)
    })
  }

  closeModel() {
    this.displayStyle = 'none'
  }

  updateCreditNotes() {
    this.isUploading = true
    var description = this.updateCreditNotesForm.get('description')?.value
    var date = this.dateFormat.convertObjectToDDMMYYYY(this.updateCreditNotesForm.get('effectiveDate')?.value)
    this.service.getUpdateCreditNotes(this.creditNotesName, description, date).subscribe({
      next: (response) => {
        this.displayStyle = 'none'
      },
      error: (e) => {
        this.isUploading = false
        if (e) {
          this.isError = true
          this.errorMessage = e
        }
      },
      complete: () => {
        this.isUploading = false
        this.isError = false
        this.clearCreditNotesList()
      },
    })
  }

  setData(data: any) {
    this.creditNotesName = data.name
  }

  updateFile() {
    this.isUploading = true
    this.service.getUpdateCreditNoteFile(this.creditNotesName, this.selectedFile).subscribe({
      next: (response) => {
        $('#updateFileModal').modal('hide');
      },
      error: (e) => {
        this.isUploading = false
        if (e) {
          this.isError = true
          this.errorMessage = e
        }
      },
      complete: () => {
        this.isUploading = false
        this.isError = false
        this.clearCreditNotesList()
      },
    })
  }

  removeCreditNote(name: any) {
    this.isUploading = true
    this.service.getRemoveCreditNotes(name).subscribe({
      next: (response) => {
        alert('Success')
      },
      error: (e) => {
        this.isUploading = false
        if (e) {
          alert(e)
        }
      },
      complete: () => {
        this.isUploading = false
        this.clearCreditNotesList()
      },
    })
  }

  handlePageChange(event: number): void {
    if (!this.loading) {
      let targetPage = event - 1;
      this.stateManager.updateState(() => ({ page: targetPage }));

      if (this.loginName !== 'ALL') {
        this.searchCreditNotesListByName(targetPage)
      } else {
        this.getAllCreditNotesList();
      }

    }
  }
}

